import type { ProjectConfigs } from "@/types";

/** The trackers a project can be configured to use. */
export type AnalyticsType = "google" | "matomo" | "gtm";

export interface AnalyticsSetup {
  type: AnalyticsType;
  /** Measurement id, GTM container id, or Matomo site id, depending on `type`. */
  id: string;
  /** Matomo only: where the instance lives. */
  matomoBaseUrl?: string;
}

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
}

/**
 * What this project has actually configured, or null if nothing usable.
 *
 * Every tracker needs an id, and Matomo needs somewhere to send to as well. A
 * project that names a tracker but not its id is a half-finished CMS entry — one
 * of the commoner ways for a config to be wrong — and initialising on it means an
 * SDK loaded, a script injected and every event dropped on the floor.
 */
export function analyticsSetup(configs: ProjectConfigs): AnalyticsSetup | null {
  const type = configs.analytics;
  const id = configs.analyticsId;

  if (!id) return null;
  if (type !== "google" && type !== "matomo" && type !== "gtm") return null;
  if (type === "matomo" && !configs.matomoBaseUrl) return null;

  return { type, id, matomoBaseUrl: configs.matomoBaseUrl };
}

/**
 * The page a hit refers to: path and query, without the origin.
 *
 * Legacy computed exactly this and then sent `window.location.hostname` instead,
 * so every Google page view recorded the same value and no path was ever
 * distinguishable from another.
 */
export function currentPage(location: {
  pathname: string;
  search: string;
}): string {
  return `${location.pathname}${location.search}`;
}
